<template>
  <b-overlay :show="loading" class="animated">
    <v-server-table
      :columns="table_config.columns"
      :options="table_config.options"
      :theme="theme"
      :ref="ref"
      id="schedule-server-table-fillable"
      class="w-100"
      @row-click="rowClick"
    >
     <span slot="date" slot-scope="{ row }">
        <router-link :to="`/schedules/${row.id}`">
          <b>{{ row.date | moment("LL") }}</b>
        </router-link>
      </span>
      <span slot="status" slot-scope="{ row }">
        <existence-gate
          :entity="row"
          :levels="table_data.status"
          :field="'status'"
        >
          <template #existing-content>
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Draft'
              "
              variant="warning"
              pill
              >Draft</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Declined'
              "
              variant="danger"
              pill
              >Declined</b-badge
            >
            <b-badge
              v-if="
                getData(row, { levels: table_data.status, field: 'status' }) ===
                'Approved'
              "
              variant="success"
              pill
              >Approved</b-badge
            >
          </template>
        </existence-gate>
      </span>
      <span slot="type" slot-scope="{ row }">
        {{ getProcessesType(row) }}
      </span>
      <span slot="sampletype" slot-scope="{ row }">
        {{ getData(row, { levels: table_data.sample_type, field: "name" }) }}
      </span>
      <span slot="samples" slot-scope="{ row }"> {{row.samples_count}} </span>
      <span slot="process" slot-scope="{ row }">
        {{
          row.getProcessSchedules().models.length
            ? getProcessesNames(row)
            : null
        }}
      </span>
      <span slot="staff" slot-scope="{ row }">
        <router-link
          v-if="row.staff"
          :to="`/customers/${getCreatorData(row, 'id')}`"
        >
          <b>{{ getCreatorData(row, "name") }}</b>
        </router-link>
      </span>
    </v-server-table>
  </b-overlay>
</template>

<script>
import Vue from "vue";
import InstrumentCapacityBar from "../instrument/InstrumentCapacityBar";
import BaseServerTable from "@/components/shared/BaseServerTable";
import ExistenceGate from "@/components/shared/ExistenceGate";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import InstrumentInstanceCollection from "@/collections/lab/InstrumentInstanceCollection";
import ScheduleCollection from "@/collections/lab/ScheduleCollection";
import ScheduleCapacityBar from "./ScheduleCapacityBar";
import { ServerTable } from "vue-tables-2";
import { getEntityData } from "@/shared/utils";
import moment from 'moment'

Vue.use(ServerTable);

export default {
  name: "ScheduleServerTableFillable",
  extends: BaseServerTable,
  components: {
    ExistenceGate,
    InstrumentCapacityBar,
    ScheduleCapacityBar,
    ServerTable,
  },
  mixins: [ToastNotifications],
  props: {
    collection: {
      type: Object,
      default: () => new ScheduleCollection(),
    },
    collection_status: {
      type: String,
      default: null,
    },
    collection_type: {
      type: String,
      default: null,
    },
    show_columns: {
      type: Array,
      default: () => [
        "date",
        "type",
        "sampletype",
        "samples",
        "process",
        "staff",
        "status",
        "note",
      ],
    },
    filter_days: {
      type: Number,
      default: () => 7
    },
    schedule_id: {
      required: true
    },
    schedule_type: {
      required: true
    }
  },
  data() {
    return {
      table_config: Object.assign(this.getDefaultTableConfig(), {
        options: {
          headings: {
            samples: "Samples",
            staff: "Author",
          },
          filterable: [],
          sortable: [],
        },
      }),
      date_format: "YYYY-MM-DD",
      table_data: {
        status: ["current_schedule_status_log", "schedule_status"],
        sample_type: ["sample_type"]
      },
    };
  },
  methods: {
    getData({ _reference }, data) {
      return getEntityData({
        entity: _reference,
        ...data,
      });
    },
    getScheduleInstruments(process) {
      return process.instrument_instances.reduce((acc, cur) => {
        acc.add(cur);

        return acc;
      }, new InstrumentInstanceCollection());
    },
    getProcessesType(schedule) {
      if (schedule.getProcessSchedules().models.length) {
        let type =
          schedule.getProcessSchedules().models[0].schedulable_process_type;
        return type.includes("Prep") ? "Prep" : "Test";
      }
      return;
    },
    getProcessesNames(schedule) {
      return schedule
        .getProcessSchedules()
        .models.map((process) =>
          process.schedulable_process
            ? process.schedulable_process.name
            : "Process unavailable"
        )
        .join(", ");
    },
    getSampleData(schedule) {
      return schedule
        .getProcessSchedules()
        .models.map((process) =>
          process.schedulable_process
            ? `${process.schedulable_process.name}: ${
                process.getSamples().models.length
              } samples`
            : "Process unavailable"
        );
    },
    getCreatorData(schedule, data) {
      return schedule.getStaff()[data];
    },
    getStatusVariant(status) {
      return status.getVariant();
    },
    prepareCollectionFilter(filter) {
      const connections = [
        "sampleType",
        "processSchedules.schedulableProcess",
        "currentScheduleStatusLog.scheduleStatus",
      ];

      connections.forEach((connection) => {
        filter.connectionAdd(connection);
      });

      filter.sortAddDesc("date", true);

      
      filter.filterLessEqual("date", moment().format(this.date_format));

      this.filter_days && 
        filter.filterGreater("date", moment().subtract(this.filter_days, 'days').format(this.date_format));
        
      this.schedule_type &&
        filter.filterEquals("sample_type_id", this.schedule_type);
      
     filter.filterEquals(
          "currentScheduleStatusLog.scheduleStatus.status",
          'Approved'
        );

      return filter;
    },
    refresh() {
      this.$refs[this.ref].refresh();
    },
    rowClick({ row }) {
        this.$emit('rowClicked', row.id);
        this.$router.push(`/schedules/${this.schedule_id}`);
    },
  },
  watch: {
    filter_days() {
      this.collection
        .getFilter()
        .filterEquals("date", moment().subtract(this.filter_days, 'days').format("YYYY-MM-DD"));
      this.refresh();
    },
    "collection.models.length"(value) {
      if (this.collection_status && this.collection_type) {
        this.$emit("updateCounter", {
          status: this.collection_status.toLowerCase(),
          type: this.collection_type.toLowerCase(),
          value,
        });
      }
    },
  },
};
</script>
